import { RaceEventDataItem } from '@app/components/race-result/model/race-event-data-item';
import { Table } from '@app/components/shared/table/model/table';
import { RiderResult } from '@app/components/race-result/model/rider-result';
import { Row } from '@app/components/shared/table/model/row';
import { ResultCode } from '@app/rest-client/faxe/models/result-code';
import { CellMeta } from '@app/components/shared/table/model/cellMeta';
import { Reported } from '@app/components/race-result/model/reported';
import { FenceResult } from '@app/rest-client/faxe/models/fence-result';
import { Fence } from '@app/rest-client/faxe/models/fence';
import { RiderEventType } from '@app/constants/rider-event-type';
import { RiderView } from '@app/facade-services/rider-view/model/rider-view';
import { distict, duration, timeString } from '@app/fremework/functions';
import { riderEventTimestamp } from '@app/domain/functions';

export const mapTable = (
  riderViews: RiderView[],
  fences: Fence[],
  resultCodes: ResultCode[]): Table<RaceEventDataItem> => {

  const results: RiderResult[] = riderViews?.map(rider => ({
    finish: riderEventTimestamp(rider.riderEvents, RiderEventType.finished),
    start: riderEventTimestamp(rider.riderEvents, RiderEventType.started),
    horseCombinationNumber: rider.horseCombinationNumber,
    isHandled: rider.isHandled,
    riderId: rider.riderGuid,
    raceClassId: rider.raceClassGuid,
    riderName: [rider.riderName, rider.horseName],
    fencesInClass: fences.filter(f => f.raceClassGuid === rider.raceClassGuid)
  }))
    .sort((a, b) => a.horseCombinationNumber - b.horseCombinationNumber);

  const allFenceResults = riderViews.flatMap(r => r.fenceResults);

  return mapRiderResult(results, fences, resultCodes, allFenceResults);
};

export const mapRiderResult = (
  items: RiderResult[],
  fences: Fence[],
  resultCodes: ResultCode[],
  fenceResults: FenceResult[]): Table<RaceEventDataItem> => ({
  showPaginator: true,
  columns: [
    { header: 'Start#', property: 'horseCombinationNumber' },
    { header: 'Ryttare', property: 'ryttare' },
    ...distict(fences.map(fence => ({
      header: fence.fenceName,
      property: fence.fenceName,
      class: 'centered-header'
    })), 'header'),
    { header: 'Tid', property: 'duration' },
    { header: 'Hanterad', property: 'isHandled' }
  ],
  rows: toRow(items, fences, fenceResults, resultCodes)
});

export const codes = (
  fence: Fence,
  fenceResultsRider: FenceResult[],
  resultCodes: ResultCode[],
  riderResult: RiderResult
): Reported => {
  const fenceNameResult = fenceResultsRider?.filter(r => r.fenceName === fence.fenceName);
  const inClass = riderResult.fencesInClass.find(fc => fc.fenceName === fence.fenceName);

  if (!inClass) {
    return { type: 'none', value: '' };
  }

  if (!fenceNameResult || fenceNameResult.length < 1) {
    return { type: 'none', value: fence.fenceName };
  }

  // Find the maximum resultGeneration
  const maxGeneration = Math.max(...fenceNameResult.map(r => r.resultGeneration || 0));

  // Filter results with the maximum resultGeneration
  const filteredResults = fenceNameResult.filter(r => r.resultGeneration === maxGeneration);

  // Check if more than one generation is present
  const hasMultipleGenerations = new Set(fenceNameResult.map(r => r.resultGeneration)).size > 1;

  const resultCodesx = filteredResults
    .map(fenceResult => resultCodes.find(c => c.resultCodeGuid === fenceResult.resultCodeGuid));

  if (resultCodesx.find(c => !c.isFault)) {
    return { type: 'good', value: true, hasMultipleGenerations };
  } else {
    return { type: 'fault', value: resultCodesx.map(r => r.shortName), hasMultipleGenerations };
  }
};

export const toRow = (
  riderResult: RiderResult[],
  fences: Fence[],
  fenceResults: FenceResult[],
  resultCodes: ResultCode[]
): Row<RaceEventDataItem>[] => riderResult ? riderResult.map(m => {
    const rowT: RaceEventDataItem = {
      finish: timeString(m.finish),
      horseCombinationNumber: m.horseCombinationNumber,
      ryttare: m.riderName.filter(part => part !== 'default'),
      id: m.horseCombinationNumber,
      isHandled: m.isHandled,
      start: timeString(m.start),
      raceClassId: m.raceClassId,
      riderId: m.riderId,
      duration: duration(m.start, m.finish),
      cellMetaMap: Map<string, CellMeta>
    };

    rowT.cellMetaMap = new Map<string, CellMeta>();
    //magic!

    if (rowT.start) {
      rowT.cellMetaMap.set('horseCombinationNumber', { icon: 'timer' });
    }

    if (rowT.duration) {
      rowT.cellMetaMap.set('duration', { icon: 'copy' });
    }

    fences.forEach(fence => {
      const reported = codes(
        fence,
        fenceResults.filter(fr => fr.horseCombinationNumber === m.horseCombinationNumber),
        resultCodes,
        m);

      rowT[fence.fenceName] = reported.value;
      rowT.cellMetaMap.set(fence.fenceName, { cellClass: reported.type, hasMultipleGenerations: reported.hasMultipleGenerations });
    });

    const rowRaceEventDataItem: Row<RaceEventDataItem> = ({
      row: rowT,
      inlineEdit: 'isHandled',
      highligt: '',
      detailTable:
        {
          showPaginator: false,
          columns: [
            { header: 'Start', property: 'start' },
            { header: 'Stop', property: 'finish' }],
          rows: [{
            row: {
              finish: rowT.finish,
              start: rowT.start
            }
          }]
        }
    });

    return rowRaceEventDataItem;
  }
) : undefined;
